// Phase 5 (web revival) — Stream C.
//
// Media surface for photos AND videos. Chrome (back button, actions,
// prev/next, keyboard nav, info drawer) lives in pages/AssetDetail.tsx —
// this component only loads bytes and renders them.
//
// Photos: wrapped in react-zoom-pan-pinch so pan/wheel-zoom/pinch all
// work for free; double-click toggles zoom. Resource bytes at
// /v1/assets/{id}/resources/{rid} require the Bearer header so we
// can't drop the URL straight into `<img src>` — instead we prefetch
// into a blob URL via api.prefetchAuthedImage. Chrome/Firefox can't
// decode HEIC natively → on a decode error we swap to the large JPEG
// thumbnail. Safari decodes HEIC natively so the original blob usually
// works there.
//
// Videos: rendered into a plain HTML5 `<video controls>`. We prefer the
// `alternate_transcoded` H.264 resource when present (browsers can't
// play HEVC `.MOV`), falling back to the primary. Same Bearer-auth
// blob-prefetch trick — `<video>` can't set Authorization on its own
// fetch any more than `<img>` can. Loading the whole file into a blob
// means we lose HTTP Range progressive playback; acceptable for the
// family-iPhone corpus (sub-100 MB clips). Larger files would want
// a query-param-token auth on the resource endpoint instead.

import { useCallback, useEffect, useState } from 'react';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import { prefetchAuthedImage } from '../../api/client';
import type { AssetDetailDTO } from '../../api/types';

interface AssetViewerProps {
  asset: AssetDetailDTO;
  /// Reports whether the photo is currently zoomed in (scale > 1). The
  /// lightbox uses this to gate touch swipe-to-navigate: a horizontal drag
  /// while zoomed is a pan, not a photo change. Stays false for videos.
  onZoomChange?: (zoomed: boolean) => void;
}

export function AssetViewer({ asset, onZoomChange }: AssetViewerProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [usingFallback, setUsingFallback] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // For videos, prefer the H.264 `alternate_transcoded` resource —
  // browsers can't play raw HEVC `.MOV`. Fall back to the primary
  // when no alternate exists. Compute the ID as a primitive string
  // so the useEffect below depends on a value React can shallow-compare;
  // a raw `asset.resources` reference here would refire the effect on
  // every refetch (even with identical contents) and flicker the image.
  const alternateVideoResourceID =
    asset.kind === 'video'
      ? asset.resources.find((r) => r.type === 'alternate_transcoded')?.id ??
        null
      : null;

  // Load the bearer-authed resource into a blob URL. Revoke on unmount /
  // src swap so we don't leak blobs across asset navigation.
  useEffect(() => {
    let cancelled = false;
    let createdURL: string | null = null;
    setSrc(null);
    setUsingFallback(false);
    setError(null);

    const resourcePath = alternateVideoResourceID
      ? `/v1/assets/${asset.id}/resources/${alternateVideoResourceID}`
      : asset.resource_url ??
        (asset.primary_resource_id
          ? `/v1/assets/${asset.id}/resources/${asset.primary_resource_id}`
          : null);

    if (!resourcePath) {
      // No primary resource (rare — backed-only edit chain or pre-bake).
      // Fall back straight to the large thumbnail.
      void (async () => {
        try {
          const url = await prefetchAuthedImage(asset.thumbnails.large);
          if (cancelled) {
            URL.revokeObjectURL(url);
            return;
          }
          createdURL = url;
          setUsingFallback(true);
          setSrc(url);
        } catch (e) {
          if (!cancelled) setError(describe(e));
        }
      })();
      return () => {
        cancelled = true;
        if (createdURL) URL.revokeObjectURL(createdURL);
      };
    }

    void (async () => {
      try {
        const url = await prefetchAuthedImage(resourcePath);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        createdURL = url;
        setSrc(url);
      } catch (e) {
        if (!cancelled) setError(describe(e));
      }
    })();

    return () => {
      cancelled = true;
      if (createdURL) URL.revokeObjectURL(createdURL);
    };
  }, [
    asset.id,
    asset.resource_url,
    asset.primary_resource_id,
    alternateVideoResourceID,
    asset.thumbnails.large,
  ]);

  // HEIC fallback: <img> failed to decode the primary resource. Replace
  // with the large JPEG thumbnail. Tracks via usingFallback so we don't
  // ping-pong if the thumbnail also fails.
  const handleImageError = useCallback(() => {
    if (usingFallback) {
      console.warn('[AssetViewer] thumbnail fallback also failed', asset.id);
      setError('Image failed to load.');
      return;
    }
    console.info(
      `[AssetViewer] HEIC primary failed to decode for asset ${asset.id}; ` +
        'falling back to large JPEG thumbnail.',
    );
    void (async () => {
      try {
        const url = await prefetchAuthedImage(asset.thumbnails.large);
        // Revoke the previous blob URL before swapping.
        if (src) URL.revokeObjectURL(src);
        setUsingFallback(true);
        setSrc(url);
      } catch (e) {
        setError(describe(e));
      }
    })();
  }, [asset.id, asset.thumbnails.large, src, usingFallback]);

  // Surface the live zoom scale to the lightbox (cheap — the parent just
  // stores it in a ref). >1.05 means a finger drag should pan, not swipe.
  const handleTransformed = useCallback(
    (_ref: unknown, state: { scale: number }) => {
      onZoomChange?.(state.scale > 1.05);
    },
    [onZoomChange],
  );

  return (
    <div className="relative h-full w-full bg-black">
      {asset.kind === 'video' ? (
        // Video render: no zoom wrapper (browser <video> handles its own
        // controls); blob URL drives src. HEIC fallback doesn't apply —
        // a video decode failure means the alternate isn't available and
        // the original was HEVC; surface the error instead of falling
        // back to a static poster.
        <div className="flex h-full w-full items-center justify-center">
          {src ? (
            <video
              key={asset.id}
              src={src}
              controls
              playsInline
              preload="metadata"
              className="media-fade-in max-h-full max-w-full select-none object-contain"
            />
          ) : error ? (
            <div className="px-6 text-center text-sm text-red-300">
              {error}
            </div>
          ) : (
            <LoadingSpinner />
          )}
        </div>
      ) : (
        <TransformWrapper
          minScale={1}
          maxScale={8}
          initialScale={1}
          wheel={{ step: 0.1 }}
          doubleClick={{ mode: 'toggle' }}
          onTransformed={handleTransformed}
          // Re-mount on asset change so pan/zoom resets.
          key={asset.id}
        >
          <TransformComponent
            wrapperClass="!h-full !w-full"
            contentClass="!h-full !w-full flex items-center justify-center"
          >
            {src ? (
              <img
                src={src}
                alt={asset.caption ?? ''}
                onError={handleImageError}
                draggable={false}
                className="media-fade-in max-h-full max-w-full select-none object-contain"
              />
            ) : error ? (
              <div className="px-6 text-center text-sm text-red-300">
                {error}
              </div>
            ) : (
              <LoadingSpinner />
            )}
          </TransformComponent>
        </TransformWrapper>
      )}
      {usingFallback && (
        <div className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 rounded-full bg-neutral-900/80 px-3 py-1 text-[11px] text-neutral-400 backdrop-blur-sm">
          JPEG preview — HEIC original
        </div>
      )}
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div
      aria-label="Loading"
      className="size-6 animate-spin rounded-full border-2 border-neutral-800 border-t-neutral-400"
    />
  );
}

function describe(e: unknown): string {
  if (e instanceof Error) return e.message;
  return 'Failed to load image.';
}
